import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTable from 'react-table';

import { StudySiteApi } from '../../../../../../api';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../common/general/Button';
import Icon from '../../../../../../common/general/Icon';
import { MANAGE_EXPECTED_SSU_SUCCESS_METRICS } from '../../../../../../constants/userOperations';
import { i18nt } from '../../../../../../root';
import { userHasAccessTo } from '../../../../../../services/auth';
import { NSC_STUDY, NSPS_SITE } from '../../../../../../services/i18n/namespaces';
import { RT_TEXT_OVERWRITE } from '../../../../../../services/i18n/react-table';

import {
  COMPLETION_AT_PSV,
  COMPLETION_AT_SIV,
  CONSENT_AT_PSV,
  CONSENT_AT_SIV,
  ENROLLMENT_AT_PSV,
  ENROLLMENT_AT_SIV,
  SCREEN_FAIL_ALLOWANCE
} from './constants';
import UpdateMetricsModal from './UpdateMetricsModal';

const columns = [
  {
    Header: i18nt('study'),
    accessor: 'study'
  },
  {
    Header: i18nt('site'),
    accessor: 'site'
  },
  {
    Header: i18nt('pcn', { ns: NSC_STUDY }),
    accessor: 'pcn'
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.screenFailAllowance', { ns: NSPS_SITE }),
    accessor: SCREEN_FAIL_ALLOWANCE
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.consentAtPsv', { ns: NSPS_SITE }),
    accessor: CONSENT_AT_PSV
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.enrollmentAtPsv', { ns: NSPS_SITE }),
    accessor: ENROLLMENT_AT_PSV
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.completionAtPsv', { ns: NSPS_SITE }),
    accessor: COMPLETION_AT_PSV
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.consentAtSiv', { ns: NSPS_SITE }),
    accessor: CONSENT_AT_SIV
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.enrollmentAtSiv', { ns: NSPS_SITE }),
    accessor: ENROLLMENT_AT_SIV
  },
  {
    Header: i18nt('expectedSsuSuccessMetrics.completionAtSiv', { ns: NSPS_SITE }),
    accessor: COMPLETION_AT_SIV
  }
];

export default function ExpectedSuccessMetricsTable({ siteDetails }) {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState({});

  const data = useMemo(
    function() {
      return [{ study: siteDetails.study_name, site: siteDetails.name, pcn: siteDetails.pcn, ...metrics }];
    },
    [metrics, siteDetails.name, siteDetails.pcn, siteDetails.study_name]
  );

  const updateValues = useCallback(() => {
    if (siteDetails.unique_identifier) {
      StudySiteApi.getMetricsById(siteDetails.unique_identifier).then(({ data }) => {
        setMetrics(data);
      });
    }
  }, [siteDetails.unique_identifier]);

  useEffect(() => {
    updateValues();
  }, [updateValues]);

  return (
    <div className="row col-12 px-0 pt-1 pb-3 m-0">
      <div className="col-md-12 row m-0 p-0 mb-2 justify-content-between border-bottom-dotted pb-0">
        <h5 className="mt-2 col-md-8 col-sm-auto p-0 c-p ">
          {t('expectedSsuSuccessMetrics.title', { ns: NSPS_SITE })}
        </h5>
        {userHasAccessTo(MANAGE_EXPECTED_SSU_SUCCESS_METRICS) && (
          <span className="update-metrics-button">
            <Button
              size="h28"
              priority="low"
              onClick={() => {
                openUpdateModal(metrics, siteDetails.unique_identifier, updateValues);
              }}
            >
              <Icon suit="glyphicon">edit</Icon>
              {t('update')}
            </Button>
          </span>
        )}
      </div>
      <div className="col-12 border px-0 site-info-table">
        <ReactTable
          {...RT_TEXT_OVERWRITE}
          data={data}
          columns={columns}
          minRows={1}
          multiSort
          className="table activity-table table-responsive-sm m-0"
          showPagination={false}
          noDataText={t('noRecordFound')}
          sortable={false}
        />
      </div>
    </div>
  );
}

function openUpdateModal(metrics, studySiteId, onClose) {
  ModalBoxes.open({
    component: <UpdateMetricsModal initialMetrics={metrics} studySiteId={studySiteId} />,
    onClose
  });
}
